import React from "react";
import moment from "moment";
import { LiaClipboardListSolid } from "react-icons/lia";
import { TbNotes } from "react-icons/tb";
import signature from "../../../assets/images/signature.png";

export default function InvoicePreview({ data, selectedBusiness, selectedCustomer }) {
    const formatAddress = (address) => {
        if (!address) return "";
        return [
            address.street || address.street1,
            address.street2,
            address.city,
            address.state,
            address.zipCode,
            address.country,
        ]
            .filter(Boolean)
            .join(", ");
    };

    const invoiceDate = data?.invoiceDate ? moment(data.invoiceDate).format("LL") : "N/A";
    const dueDate = data?.invoiceDueDate ? moment(data.invoiceDueDate).format("LL") : "N/A";

    return (
        <div className="invoice-preview-container p-4 p-md-5">
            <div className="invoice-preview-paper shadow-sm bg-white p-4 p-md-5 mx-auto" style={{ maxWidth: "800px", minHeight: "1000px" }}>
                {/* Header Section */}
                <div className="d-flex justify-content-between align-items-start mb-5 border-bottom pb-4">
                    <div>
                        <h2 className="fw-bold text-primary mb-1">INVOICE</h2>
                        <h5 className="text-secondary mb-0">#{data?.id || "PREVIEW"}</h5>
                    </div>
                    <div className="text-end">
                        <h4 className="fw-bold mb-1">{selectedBusiness?.name || "Business Name"}</h4>
                        <p className="small text-muted mb-0">{selectedBusiness?.email}</p>
                    </div>
                </div>

                {/* Info Grid */}
                <div className="row mb-5 gy-4">
                    <div className="col-sm-6">
                        <h6 className="fw-bold text-muted text-uppercase small mb-3">Bill To</h6>
                        <h5 className="fw-bold mb-1">
                            {selectedCustomer?.label || "Select Customer"}
                        </h5>
                        <p className="small text-muted mb-1">{selectedCustomer?.email}</p>
                        <p className="small text-muted mb-0">
                            {selectedCustomer?.street && `${selectedCustomer.street}, `}
                            {selectedCustomer?.city && `${selectedCustomer.city}, `}
                            {selectedCustomer?.state && `${selectedCustomer.state}`}
                        </p>
                    </div>
                    <div className="col-sm-6 text-sm-end">
                        <div className="mb-3">
                            <h6 className="fw-bold text-muted text-uppercase small mb-1">Due Date</h6>
                            <p className="fw-bold mb-0">{dueDate}</p>
                        </div>
                        <div>
                            <h6 className="fw-bold text-muted text-uppercase small mb-1">Subject</h6>
                            <p className="fw-bold mb-0 text-break">{data?.notes || "General Service"}</p>
                        </div>
                    </div>
                </div>

                {/* Table Section */}
                <div className="table-responsive mb-5">
                    <table className="table">
                        <thead className="table-light">
                            <tr className="small text-uppercase">
                                <th className="border-0">Description</th>
                                <th className="border-0 text-center">Qty</th>
                                <th className="border-0 text-end">Unit Price</th>
                                <th className="border-0 text-end">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data?.productDetail?.map((item, index) => (
                                <tr key={index}>
                                    <td className="border-bottom py-3">
                                        <p className="fw-bold mb-0">{item.productName || "Product Name"}</p>
                                    </td>
                                    <td className="border-bottom py-3 text-center align-middle">{item.quantity}</td>
                                    <td className="border-bottom py-3 text-end align-middle">${parseFloat(item.rate).toFixed(2)}</td>
                                    <td className="border-bottom py-3 text-end align-middle">${parseFloat(item.total).toFixed(2)}</td>
                                </tr>
                            ))}
                            {!data?.productDetail?.length && (
                                <tr>
                                    <td colSpan="4" className="text-center py-4 text-muted small">No items added yet</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Summary & Totals */}
                <div className="row justify-content-end mb-5">
                    <div className="col-sm-6 col-md-5">
                        <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted">Subtotal</span>
                            <span className="fw-bold">${data?.netAmount}</span>
                        </div>
                        {(data?.appliedDiscounts?.length > 0) ? data.appliedDiscounts.map((discount, di) => (
                            <div key={di} className="d-flex justify-content-between mb-2 text-danger">
                                <span>{discount.name} ({discount.type === 'flat' ? `$${discount.rate}` : `${discount.rate}%`})</span>
                                <span>-${parseFloat(discount.amount || 0).toFixed(2)}</span>
                            </div>
                        )) : (data?.invoiceDiscount > 0 && (
                            <div className="d-flex justify-content-between mb-2 text-danger">
                                <span>Discounts</span>
                                <span>-${parseFloat(data.invoiceDiscount).toFixed(2)}</span>
                            </div>
                        ))}
                        {data?.invoiceTax > 0 && data.appliedTaxes?.map((tax, ti) => (
                            <div key={ti} className="d-flex justify-content-between mb-2">
                                <span>{tax.name} ({tax.rate}%)</span>
                                <span>+${parseFloat(tax.amount || ((data.netAmount - data.invoiceDiscount) * tax.rate / 100)).toFixed(2)}</span>
                            </div>
                        ))}
                        {data?.shippingCharge > 0 && (
                            <div className="d-flex justify-content-between mb-2">
                                <span>Shipping</span>
                                <span>+${data?.shippingCharge}</span>
                            </div>
                        )}
                        <div className="d-flex justify-content-between border-top pt-3 mt-3">
                            <h5 className="fw-heavy">Amount due</h5>
                            <h5 className="fw-heavy text-primary">${data?.totalAmount}</h5>
                        </div>
                    </div>
                </div>

                {/* Footer / Notes */}
                <div className="mt-auto border-top pt-4">
                    <div className="row">
                        <div className="col-sm-8">
                            {data?.terms && (
                                <div className="mb-3">
                                    <h6 className="fw-bold small text-muted text-uppercase mb-2">Terms & Conditions</h6>
                                    <p className="small text-muted lh-sm">{data.terms}</p>
                                </div>
                            )}
                            {data?.notes && (
                                <div>
                                    <h6 className="fw-bold small text-muted text-uppercase mb-2">Notes</h6>
                                    <p className="small text-muted lh-sm">{data.notes}</p>
                                </div>
                            )}
                        </div>
                        <div className="col-sm-4 text-end">
                            <h6 className="fw-bold small text-muted text-uppercase mb-2">Authorized Signature</h6>
                            <img src={signature} alt="signature" className="img-fluid" style={{ maxWidth: "120px", filter: "grayscale(100%)" }} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
